import { Component, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { SpinnerOverlayService } from '@deltager/organizers/admin/shared/spinner-overlay/spinner-overlay.service';
import { TranslateModule } from '@ngx-translate/core';
import { PaymentServiceProviderStatus } from '../payment-service-provider.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CommonModule } from '@angular/common';
import { AddNewAccountComponent } from '../../settlement-account/add-new-account/add-new-account.component';
import { PaymentType } from '@deltager/organizers/admin/shared/models/PaymentType';
import { EventType } from '@angular/router';
import { BaseDialogComponent } from '@deltager/organizers/admin/shared/components/base-dialog/base-dialog.component';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import { AuthService } from '@deltager/organizers/admin/core/auth/auth.service';
import { UserService } from '@deltager/organizers/admin/core/services/user.service';
import { environment } from '@deltager/organizers/admin/environments/environment';

export class OnboardDialogComponentData {
  returnUrl?: string;
  eventId?: number;
  eventType?: number;
  status?: PaymentServiceProviderStatus;
}

@Component({
  selector: 'app-onboard-dialog',
  standalone: true,
  imports: [
    TranslateModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    CommonModule,
    AddNewAccountComponent,
    BaseDialogComponent,
  ],
  templateUrl: './onboard-dialog.component.html',
  styleUrl: './onboard-dialog.component.scss',
})
export class OnboardDialogComponent {
  isLoading = false;
  needsABankAccount: boolean;
  paymentType: PaymentType;
  eventId?: number;
  eventType?: EventType;
  PaymentType = PaymentType;
  onboarding = false;
  isLoadingStripeData = false;

  @ViewChild('addNewAccountComponent')
  private addNewAccountComponent: AddNewAccountComponent;

  constructor(
    private dialogRef: MatDialogRef<OnboardDialogComponent>,
    spinnerService: SpinnerOverlayService,
    @Inject(MAT_DIALOG_DATA) private data: OnboardDialogComponentData,
    private authService: AuthService,
    private userService: UserService,
  ) {
    spinnerService.hide();

    this.needsABankAccount = data.status.needsABankAccount;
    this.paymentType = data.status.paymentType;
    this.eventId = data.eventId;
    this.eventType = data.eventType as EventType;
  }

  async continue(): Promise<void> {
    this.setLoading(true);

    try {
      if (this.needsABankAccount) {
        this.addNewAccountComponent.accountFormGroup.disable();
        this.addNewAccountComponent.updateAccount(false);
        return;
      }

      this.onboarding = true;
      await this.loadStripeOnboarding();
    } catch {
      if (this.needsABankAccount) {
        this.addNewAccountComponent.accountFormGroup.enable();
      }
      this.setLoading(false);
    }
  }

  async loadStripeOnboarding(): Promise<void> {
    this.isLoadingStripeData = true;

    const organizerId = this.userService.OrganizerId;

    const body = {
      paymentType: this.paymentType,
      eventId: this.data.eventId,
      eventType: this.data.eventType,
    };

    const token = this.authService.getAuthToken();

    const response = await fetch(`${environment.apiBaseURL}/payment-service-provider/${organizerId}/account_session`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      return undefined;
    }

    const result = await response.json();

    const stripeConnectInstance = loadConnectAndInitialize({
      publishableKey: result.publishableKey,
      fetchClientSecret: () => Promise.resolve(result.clientSecret),
    });

    const onboardComponent = stripeConnectInstance.create('account-onboarding');

    const container = document.getElementById('stripe-onboarding-container');

    onboardComponent.setOnExit(() => {
      window.location.href = this.data.returnUrl;
    });

    this.isLoadingStripeData = false;
    container?.appendChild(onboardComponent);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  setLoading(loading: boolean): void {
    this.isLoading = loading;
    this.dialogRef.disableClose = loading;
  }

  async onAccountAddedOrUpdated(): Promise<void> {
    try {
      this.onboarding = true;
      await this.loadStripeOnboarding();
    } catch {
      this.setLoading(false);
      this.addNewAccountComponent.accountFormGroup.enable();
    }
  }

  onAccountCreationError(): void {
    this.setLoading(false);
    this.addNewAccountComponent.accountFormGroup.enable();
  }

  get isContinueButtonDisabled(): boolean {
    return this.isLoading || (this.addNewAccountComponent && !this.addNewAccountComponent.accountFormGroup.valid);
  }
}
